import React, { useMemo } from "react";
import "./PrivacyPolicy.css";
import { useLocalContext } from "../../context/LocalContext";

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients worldwide",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const sections = [
    { id: "overview", label: "Overview & scope", index: "01" },
    { id: "data-we-collect", label: "Data we collect", index: "02" },
    { id: "how-we-use-data", label: "How we use data", index: "03" },
    { id: "legal-bases", label: "Lawful bases", index: "04" },
    { id: "cookies", label: "Cookies & tracking", index: "05" },
    { id: "sharing", label: "Sharing & processors", index: "06" },
    { id: "transfers", label: "International transfers", index: "07" },
    { id: "retention", label: "Data retention", index: "08" },
    { id: "rights", label: "Your rights", index: "09" },
    { id: "security", label: "Security", index: "10" },
    { id: "children", label: "Children’s data", index: "11" },
    { id: "changes-contact", label: "Changes & contact", index: "12" },
  ];

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Privacy & data use</p>
          <div className="pp-header-main">
            <div>
              <h1>Privacy Policy</h1>
              <p>
                At {brand.name}, we treat your data with the same care we bring to our work. This
                page explains what we collect, how we use it, and the options you have when you
                visit our website or work with us.
              </p>
            </div>
            <div className="pp-header-meta-card">
              <div className="meta-row">
                <span className="meta-label">Controller</span>
                <span className="meta-value">{brand.name}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Last updated</span>
                <span className="meta-value">{effectiveDate}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Layout: sidebar nav + content */}
        <div className="pp-layout">
          {/* Outline / sidebar */}
          <aside className="pp-nav" aria-label="Privacy policy outline">
            <h2 className="pp-nav-title">On this page</h2>
            <ol className="pp-nav-list">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="pp-nav-index">{s.index}</span>
                    <span className="pp-nav-label">{s.label}</span>
                  </a>
                </li>
              ))}
            </ol>
            <div className="pp-nav-footnote">
              <p>
                This page is meant as a clear, human-readable summary of how we treat personal
                data. It does not replace any mandatory legal terms in a signed agreement.
              </p>
            </div>
          </aside>

          {/* Main content card */}
          <div className="privacy-policy-content">
            <section id="overview">
              <h2>01. Overview & scope</h2>
              <p>
                This Privacy Policy describes how {brand.name} (“we”, “us”, or “our”) handles
                personal information when you use our website, complete a form, interact with our
                campaigns, or engage us for services.
              </p>
              <p>
                By using our website or working with us, you acknowledge that you’ve read this
                policy and understand how we process personal data, in line with applicable laws.
              </p>
            </section>

            <section id="data-we-collect">
              <h2>02. Data we collect</h2>
              <p>We collect information in a few different ways:</p>
              <ul>
                <li>
                  <strong>Information you provide directly</strong> – for example, when you
                  complete a contact form, request a proposal, join a call, or email us. This can
                  include your name, email, phone number, company, job title, and project details.
                </li>
                <li>
                  <strong>Usage & technical data</strong> – such as IP address, device type,
                  browser, operating system, pages visited, referring URLs, and timestamps. This is
                  typically collected via analytics tools.
                </li>
                <li>
                  <strong>Cookies and similar technologies</strong> – small files or tags that help
                  us remember preferences, understand site performance, and improve the experience.
                </li>
              </ul>
            </section>

            <section id="how-we-use-data">
              <h2>03. How we use your data</h2>
              <p>We use personal data to:</p>
              <ul>
                <li>Respond to inquiries, proposals, and support requests.</li>
                <li>
                  Plan, deliver, and improve our services, projects, and client communication.
                </li>
                <li>
                  Understand how our website and campaigns are performing, so we can make them more
                  useful.
                </li>
                <li>
                  Send relevant service-related information, such as project updates or meeting
                  invites.
                </li>
                <li>
                  Maintain security, prevent fraud or misuse, and comply with legal and contractual
                  obligations.
                </li>
              </ul>
            </section>

            <section id="legal-bases">
              <h2>04. Legal bases for processing</h2>
              <p>
                Where data-protection laws such as the GDPR or UK GDPR apply, we rely on one or
                more of the following legal bases:
              </p>
              <ul>
                <li>
                  <strong>Consent</strong> – when you actively share information with us (for
                  example, via a form) or accept certain optional cookies.
                </li>
                <li>
                  <strong>Legitimate interests</strong> – for running and improving our business,
                  communicating with prospective and existing clients, and keeping our systems
                  secure, where these interests are not overridden by your rights.
                </li>
                <li>
                  <strong>Contract</strong> – when processing is necessary to deliver a project or
                  service you have requested.
                </li>
                <li>
                  <strong>Legal obligations</strong> – when we must retain or disclose data for
                  compliance, tax, or regulatory reasons.
                </li>
              </ul>
            </section>

            <section id="cookies">
              <h2>05. Cookies & tracking technologies</h2>
              <p>
                Our website may use cookies, pixels, tags, or similar technologies to understand how
                visitors use the site and to ensure it functions correctly.
              </p>
              <p>
                Depending on your location, you may see a cookie banner that allows you to manage
                optional cookies. You can also control cookies through your browser settings. If you
                block certain cookies, some parts of the website may not work as intended.
              </p>
            </section>

            <section id="sharing">
              <h2>06. Sharing information with others</h2>
              <p>
                We do <strong>not</strong> sell your personal data. We may, however, share limited
                information with:
              </p>
              <ul>
                <li>Hosting, infrastructure, and email providers.</li>
                <li>Analytics, performance, and CRM platforms.</li>
                <li>Payment and invoicing tools (for paid engagements).</li>
                <li>Professional advisors (for example, legal or accounting support).</li>
              </ul>
              <p>
                These third parties only receive the data they need to perform their services and
                are expected to handle it securely and in accordance with applicable data-protection
                laws.
              </p>
            </section>

            <section id="transfers">
              <h2>07. International data transfers</h2>
              <p>
                Some of our tools and service providers may be located outside your country or
                region. As a result, your data may be processed in countries with different
                data-protection standards than your own.
              </p>
              <p>
                When required, we use appropriate safeguards—such as standard contractual clauses or
                equivalent mechanisms—to help ensure an adequate level of protection for personal
                data transferred internationally.
              </p>
            </section>

            <section id="retention">
              <h2>08. Data retention</h2>
              <p>
                We keep personal data only for as long as it is reasonably necessary for the
                purposes described in this policy, or as required by law (for example, for tax,
                accounting, or audit obligations).
              </p>
              <p>
                Once data is no longer needed, we aim to delete it securely or anonymize it so that
                it can no longer be linked back to an identifiable person.
              </p>
            </section>

            <section id="rights">
              <h2>09. Your rights and choices</h2>
              <p>
                Depending on where you live and the laws that apply, you may have some or all of the
                following rights regarding your personal data:
              </p>
              <ul>
                <li>To request access to the personal data we hold about you.</li>
                <li>To ask us to correct inaccurate or incomplete information.</li>
                <li>To request deletion of your personal data in certain circumstances.</li>
                <li>
                  To object to or request restrictions on certain types of processing, including
                  direct marketing.
                </li>
                <li>To request a copy of your data in a portable format.</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the details below. We may need to
                verify your identity before responding and may not always be able to fulfil every
                request, for example where we must keep data for legal reasons.
              </p>
            </section>

            <section id="security">
              <h2>10. How we protect your data</h2>
              <p>
                We use reasonable technical and organizational measures to help protect personal
                data from unauthorized access, loss, misuse, or alteration. This can include access
                controls, encryption in transit where appropriate, and restricted internal access.
              </p>
              <p>
                No system or transmission method is completely risk-free, and we cannot guarantee
                absolute security. However, we work to keep risks proportionate and under regular
                review.
              </p>
            </section>

            <section id="children">
              <h2>11. Children’s data</h2>
              <p>
                Our website and services are designed for adults and business users. We do not
                knowingly collect personal information from children under 13 (or under 16 where
                different local rules apply).
              </p>
              <p>
                If you believe a child has provided us with personal data, please contact us so that
                we can review and, where appropriate, delete it.
              </p>
            </section>

            <section id="changes-contact">
              <h2>12. Changes to this policy & how to contact us</h2>
              <p>
                We may update this Privacy Policy from time to time—for example, if our services or
                applicable laws change. When we make significant updates, we will adjust the “Last
                updated” date at the top of this page and may provide additional notice where
                appropriate.
              </p>
              <p>If you have questions, concerns, or requests about this policy or your data, you can contact us at:</p>
              <ul className="pp-contact-list">
                <li>
                  <strong>Email:</strong>{" "}
                  <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                    {brand.email}
                  </a>
                </li>
                <li>
                  <strong>Phone:</strong>{" "}
                  <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                    {brand.phone}
                  </a>
                </li>
                <li>
                  <strong>Address:</strong> {brand.address}
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
